import type { FC, ReactNode } from 'react'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import { SONG } from '@/constants/birthday'

interface SongContextValue {
    playing: boolean
    current: number
    duration: number
    failed: boolean
    toggle: () => void
    pause: () => void
    seek: (time: number) => void
}

interface SongProviderProps {
    children: ReactNode
}

const SongContext = createContext<SongContextValue | null>(null)

// One audio element for the whole page, so the song keeps playing while she scrolls.
export const SongProvider: FC<SongProviderProps> = ({ children }): ReactNode => {
    const audioRef = useRef<HTMLAudioElement>(null)
    const [playing, setPlaying] = useState(false)
    const [current, setCurrent] = useState(0)
    const [duration, setDuration] = useState(0)
    const [failed, setFailed] = useState(false)

    const readDuration = useCallback((audio: HTMLAudioElement): void => {
        if (Number.isFinite(audio.duration) && audio.duration > 0) setDuration(audio.duration)
    }, [])

    useEffect(() => {
        const audio = audioRef.current

        if (audio && audio.readyState >= 1) readDuration(audio)
    }, [readDuration])

    const toggle = useCallback((): void => {
        const audio = audioRef.current

        if (!audio) return

        if (!audio.paused) {
            audio.pause()
            return
        }

        document.querySelectorAll('video').forEach((video) => video.pause())
        setFailed(false)
        audio.play().catch(() => setFailed(true))
    }, [])

    const pause = useCallback((): void => {
        audioRef.current?.pause()
    }, [])

    const seek = useCallback((time: number): void => {
        const audio = audioRef.current

        if (!audio) return

        audio.currentTime = time
        setCurrent(time)
    }, [])

    const value = useMemo(() => ({
        playing,
        current,
        duration,
        failed,
        toggle,
        pause,
        seek
    }), [playing, current, duration, failed, toggle, pause, seek])

    return (
        <SongContext.Provider value={value}>
            {children}

            <audio
                ref={audioRef}
                src={SONG.src}
                preload='metadata'
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={() => {
                    setPlaying(false)
                    setCurrent(0)
                }}
                onTimeUpdate={(event) => setCurrent(event.currentTarget.currentTime)}
                onLoadedMetadata={(event) => readDuration(event.currentTarget)}
                onDurationChange={(event) => readDuration(event.currentTarget)}
                onError={() => setFailed(true)}
            />
        </SongContext.Provider>
    )
}

export const useSong = (): SongContextValue => {
    const context = useContext(SongContext)

    if (!context) throw new Error('useSong must be used inside SongProvider')

    return context
}
